import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import InstallPrompt from "./components/InstallPrompt";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Workers from "./pages/Workers";
import Attendance from "./pages/Attendance";
import Leaves from "./pages/Leaves";
import Advances from "./pages/Advances";
import Payouts from "./pages/Payouts";
import Reports from "./pages/Reports";
import AuthCallback from "./pages/AuthCallback";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import OAuthBridge from "./pages/OAuthBridge";

const TOAST_OPTIONS = {
  style: {
    fontFamily: 'IBM Plex Sans, sans-serif',
  },
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F0]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E4632]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F0]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E4632]"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const location = useLocation();

  // Google Analytics: track SPA route changes as page views
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);

  // CRITICAL: Detect session_id in URL fragment BEFORE routing.
  // This must happen synchronously during render, not in useEffect.
  // BUT skip this shortcut on the /oauth-bridge route — that page needs to render its own
  // native deep-link logic and must NOT be replaced by AuthCallback.
  if (
    location.hash?.includes('session_id=') &&
    location.pathname !== '/oauth-bridge'
  ) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/workers" element={
        <ProtectedRoute>
          <Workers />
        </ProtectedRoute>
      } />
      <Route path="/attendance" element={
        <ProtectedRoute>
          <Attendance />
        </ProtectedRoute>
      } />
      <Route path="/leaves" element={
        <ProtectedRoute>
          <Leaves />
        </ProtectedRoute>
      } />
      <Route path="/advances" element={
        <ProtectedRoute>
          <Advances />
        </ProtectedRoute>
      } />
      <Route path="/payouts" element={
        <ProtectedRoute>
          <Payouts />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <Reports />
        </ProtectedRoute>
      } />
      {/* Pricing route hidden from nav but kept accessible via direct URL for legacy checkout links */}
      <Route path="/pricing" element={
        <ProtectedRoute>
          <Pricing />
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/oauth-bridge" element={<OAuthBridge />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Register service worker for PWA (production only — CRA dev server does not serve SW correctly)
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch((err) => {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('SW registration failed:', err?.message);
          }
        });
      });
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <InstallPrompt />
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={TOAST_OPTIONS}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
